import { useSyncExternalStore } from 'react'

/**
 * Light and dark. Two states, and light is what an untouched install runs on.
 *
 * There used to be a third — `system`, following `prefers-color-scheme` — and it
 * was the default. It is gone deliberately. Rawam is an admin product used on
 * managed machines whose OS preference is rarely a considered choice, and a
 * panel that arrives dark because a laptop switched at sunset is not what the
 * person who opened it asked for. Two states also mean the control can be READ:
 * with three, "follows my system, which is currently dark" and "pinned dark"
 * looked identical on screen.
 *
 * So the theme is now exactly what the user last pressed, or light.
 *
 * Unlike the language, this does NOT reload. A theme is only custom properties
 * on `:root`, every component already reads them, and a page that blinks when
 * you press a light switch feels broken. The two things that cannot read a CSS
 * variable — the charts' canvas and the browser's own form controls — are
 * handled: `color-scheme` covers the second, and Chart.jsx subscribes for the
 * first.
 */

const STORAGE_KEY = 'rawam.theme'
const CHOICES = ['light', 'dark']
const DEFAULT = 'light'

function read() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY)
    if (CHOICES.includes(saved)) return saved
  } catch {
    /* storage unavailable (private mode) — the default still applies */
  }
  return DEFAULT
}

let theme = read()
const listeners = new Set()

export const getTheme = () => theme
export const isDark = () => theme === 'dark'

function apply() {
  const root = document.documentElement
  root.dataset.theme = theme
  /* Tells the browser to draw ITS surfaces — scrollbars, the date picker's
     native fallback, form controls, the canvas behind an unpainted page — to
     match. Without it a dark app keeps a white scrollbar and white select
     popups, which is the giveaway of a theme applied only to what CSS reaches. */
  root.style.colorScheme = theme
  for (const listener of listeners) listener(theme)
}

export function setTheme(pick) {
  if (!CHOICES.includes(pick)) return
  theme = pick
  try {
    /* Light is STORED rather than treated as "no preference". It is a choice
       like any other, and a user who turns a dark panel back to light expects
       it to still be light tomorrow. */
    localStorage.setItem(STORAGE_KEY, pick)
  } catch {
    /* see read() */
  }
  apply()
}

export const nextTheme = () => (theme === 'dark' ? 'light' : 'dark')

export function subscribe(listener) {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/**
 * For the few components that have to answer in JavaScript rather than in CSS.
 *
 * That is the charts and nothing else: a canvas cannot read a custom property,
 * so an ECharts option carries resolved colour strings, and the option has to be
 * rebuilt when those change. Calling this re-renders the component on a switch,
 * which rebuilds its option against the new palette.
 *
 * Everything else in the app is styled in CSS and needs nothing — which is the
 * point of putting the theme in custom properties.
 */
export function useTheme() {
  return useSyncExternalStore(subscribe, getTheme, () => DEFAULT)
}

/**
 * Called once before first paint, alongside bootBranding and bootLocale.
 *
 * index.html carries no theme, so without this a dark install paints light and
 * then corrects itself — a white flash on every load, which is exactly the thing
 * a dark theme exists to avoid.
 */
export function bootTheme() {
  apply()
}
